import React, { MouseEvent, useCallback, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";

import Icon, { IconSlug } from "../../../../../shared/components/design-system/Icon";
import Tooltip from "../../design-system/Tooltip";
import "./styles.scss";

export default function NavigationItem({
  title,
  closeMobileNavigation,
  icon,
  onClick,
  to,
  subPages = [], // Other 'folders' that cause this tab to highlight as active
  neverActive,
  isNavCollapsed,
}: {
  to: string;
  subPages?: string[];
  title: string;
  onClick?: (event: MouseEvent) => void;
  icon: IconSlug;
  neverActive?: boolean;
  closeMobileNavigation: () => void;
  isNavCollapsed: boolean;
}) {
  const navItemRef = useRef<HTMLLIElement>(null);
  const isInternal = to.startsWith("/");

  const handleNavLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      closeMobileNavigation();
      if (isInternal) {
        (event.target as HTMLAnchorElement).blur();
      }
    },
    [isInternal, onClick, closeMobileNavigation],
  );

  // NavLink can calculate isActive for the main target, but we have to do it anyway for the sub-pages so we might as well use the same code for both.
  const { pathname } = useLocation();
  const isActive =
    !neverActive && isInternal && [to, ...subPages].some((url) => pathname === url || pathname.startsWith(`${url}/`));

  return (
    <li ref={navItemRef} className="c-navigation__item">
      <NavLink
        onClick={handleNavLinkClick}
        className={`c-navigation__link ${isActive ? "is-active" : ""}`}
        to={`${to}#main-content`}
        target={isInternal ? undefined : "_blank"}
      >
        <NavLinkContent icon={icon} title={title} />
        {isNavCollapsed ? <Tooltip anchorRef={navItemRef}>{title}</Tooltip> : null}
      </NavLink>
    </li>
  );
}

export function NavLinkContent({ icon, title }: { icon: IconSlug; title: string }) {
  return (
    <>
      <span className="c-navigation__link-icon" aria-hidden="true">
        <Icon icon={icon} />
      </span>
      <span className="c-navigation__link-label">{title}</span>
    </>
  );
}
